import { useEffect, useState } from "react";
import { apiGet } from "@/lib/api";
import type { ClubInfo } from "@/lib/types";

// club.php 는 헤더·히어로·푸터·홈 등 여러 곳에서 필요하다.
// 화면마다 따로 부르면 같은 요청이 중복되므로, 모듈 단위 캐시로 «한 번만» 불러와 공유한다.
let clubCache: ClubInfo | null = null;
let clubPromise: Promise<ClubInfo> | null = null;

function loadClub(): Promise<ClubInfo> {
  if (clubCache) return Promise.resolve(clubCache);
  if (!clubPromise) {
    clubPromise = apiGet<ClubInfo>("club.php")
      .then((res) => {
        clubCache = res.data;
        return res.data;
      })
      .catch((err) => {
        // 실패하면 캐시를 비워 다음 마운트에서 다시 시도할 수 있게 한다.
        clubPromise = null;
        throw err;
      });
  }
  return clubPromise;
}

interface UseClubResult {
  club: ClubInfo | null;
  loading: boolean;
}

/**
 * club.php 를 공유 캐시로 1회 조회하는 훅.
 * 이미 받아온 값이 있으면 즉시 반환하고 추가 요청을 하지 않는다.
 */
export function useClub(): UseClubResult {
  const [club, setClub] = useState<ClubInfo | null>(clubCache);
  const [loading, setLoading] = useState<boolean>(!clubCache);

  useEffect(() => {
    if (clubCache) {
      setClub(clubCache);
      setLoading(false);
      return;
    }
    let active = true;
    setLoading(true);
    loadClub()
      .then((data) => {
        if (active) setClub(data);
      })
      .catch(() => {
        if (active) setClub(null);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, []);

  return { club, loading };
}